// Module catalog — ported from design/shell.jsx.
// Each module maps to a frontend route under /app/<path>.

import type { IconName } from "../shared/Icon";

export type ModuleSection = "home" | "collab" | "work" | "ops" | "exec" | "etc";

export type Module = {
  id: string;
  label: string;
  icon: IconName;
  color: string;
  path: string;
  badge?: number;
  section: ModuleSection;
  menuKey?: string; // matches Manifest.menus
  execOnly?: boolean; // hidden unless user has C_LEVEL / SUPER_ADMIN
};

export const MODULE_SECTIONS = [
  { id: "home",   label: "홈",       color: "#6E6FCF" },
  { id: "collab", label: "협업",     color: "#0891B2" },
  { id: "work",   label: "업무",     color: "#D97706" },
  { id: "ops",    label: "운영",     color: "#16A34A" },
  { id: "exec",   label: "내부운영", color: "#DC2626" },
  { id: "etc",    label: "기타",     color: "#94A3B8" },
] as const;

export const MODULES: Module[] = [
  // ── 홈 ──────────────────────────────────────────────────────────────
  { id: "dashboard", label: "대시보드", icon: "Home",      color: "var(--m-dashboard)", path: "/dashboard",     section: "home",   menuKey: "dashboard" },
  { id: "notice",    label: "공지",     icon: "Megaphone", color: "#0891B2",            path: "/notice",        section: "home",   menuKey: "notice" },
  { id: "planning",  label: "timple",   icon: "Sparkles",  color: "#6E6FCF",            path: "/planning",      section: "home" },

  // ── 협업 ─────────────────────────────────────────────────────────────
  { id: "mail",      label: "메일",     icon: "Mail",      color: "var(--m-collab)",    path: "/mail",          section: "collab", menuKey: "mail" },
  { id: "msg",       label: "채팅",     icon: "Msg",       color: "var(--m-collab)",    path: "/chat",          section: "collab", menuKey: "messenger" },
  { id: "cal",       label: "캘린더",   icon: "Cal",       color: "var(--m-collab)",    path: "/calendar",      section: "collab", menuKey: "calendar" },

  // ── 업무 ─────────────────────────────────────────────────────────────
  { id: "proj",      label: "프로젝트", icon: "Todo",      color: "var(--m-task)",      path: "/projects",      section: "work",   menuKey: "projects" },
  { id: "todos",     label: "할 일",    icon: "Check",     color: "var(--m-task)",      path: "/todos",         section: "work",   menuKey: "todos" },
  { id: "drive",     label: "드라이브", icon: "Folder",    color: "var(--m-task)",      path: "/drive",         section: "work",   menuKey: "drive" },
  { id: "approval",  label: "전자결재",  icon: "Approve",   color: "var(--m-approval)",  path: "/approval",      section: "work",   menuKey: "approval" },
  { id: "ref",       label: "레퍼런스", icon: "Star",      color: "var(--m-task)",      path: "/references",    section: "work",   menuKey: "drive" },
  { id: "programs",  label: "프로그램", icon: "Film",      color: "var(--m-task)",      path: "/programs",      section: "work",   menuKey: "programs" },
  { id: "docstudio", label: "통합 문서 작성", icon: "Docs", color: "#6E6FCF",           path: "/docstudio",     section: "work" },

  // ── 운영 ─────────────────────────────────────────────────────────────
  { id: "favorite",  label: "즐겨찾기", icon: "Star",      color: "#16A34A",            path: "/favorites",     section: "ops",    menuKey: "dashboard" },
  { id: "board",     label: "게시판",   icon: "Megaphone", color: "#16A34A",            path: "/board",         section: "ops",    menuKey: "dashboard" },
  { id: "att",       label: "근태",     icon: "Clock",     color: "var(--m-hrm)",       path: "/attendance",    section: "ops",    menuKey: "attendance" },
  { id: "leave",     label: "휴가",     icon: "Palm",      color: "var(--m-hrm)",       path: "/leave",         section: "ops",    menuKey: "leave" },
  { id: "dir",       label: "임직원",   icon: "Users",     color: "var(--m-hrm)",       path: "/directory",     section: "ops",    menuKey: "directory" },
  { id: "hr",        label: "급여",     icon: "Wallet",    color: "var(--m-hrm)",       path: "/payroll",       section: "ops",    menuKey: "hr_admin" },
  { id: "expense",   label: "경비",     icon: "Receipt",   color: "var(--m-finance)",   path: "/expense",       section: "ops",    menuKey: "expense" },
  { id: "crm",       label: "거래처",   icon: "Building",  color: "var(--m-crm)",       path: "/crm",           section: "ops",    menuKey: "crm" },
  { id: "finance",   label: "재무",     icon: "Wallet",    color: "var(--m-finance)",   path: "/finance",       section: "ops",    menuKey: "finance_admin" },
  { id: "bi",        label: "경영 BI",  icon: "Chart",     color: "var(--m-bi)",        path: "/executive",     section: "ops",    menuKey: "executive_bi" },

  // ── 내부운영 (임원 전용, 빨간 섹션) ──────────────────────────────────
  { id: "exec_hr",   label: "인사관리", icon: "Users",     color: "#DC2626",            path: "/exec/hr",       section: "exec",   menuKey: "executive_bi", execOnly: true },
  { id: "exec_biz",  label: "내부경영", icon: "Chart",     color: "#DC2626",            path: "/exec/biz",      section: "exec",   menuKey: "executive_bi", execOnly: true },
  { id: "exec_docs", label: "내부문서", icon: "Folder",    color: "#DC2626",            path: "/exec/docs",     section: "exec",   menuKey: "executive_bi", execOnly: true },

  // ── 기타 ─────────────────────────────────────────────────────────────
  { id: "notif",     label: "알림",     icon: "Bell",      color: "#475569",            path: "/notifications", section: "etc",    menuKey: "dashboard" },
  { id: "settings",  label: "환경설정", icon: "User",      color: "#6E6FCF",            path: "/settings",      section: "etc",    menuKey: "dashboard" },
];

export const ADMIN_MODULE: Module = {
  id: "admin",
  label: "시스템 관리",
  icon: "Settings",
  color: "var(--m-system)",
  path: "/admin",
  section: "etc",
  menuKey: "system_admin",
};

export function moduleById(id: string): Module | undefined {
  return [...MODULES, ADMIN_MODULE].find((m) => m.id === id);
}

export function moduleByPath(pathname: string): Module | undefined {
  return [...MODULES, ADMIN_MODULE].find((m) => pathname.startsWith(m.path));
}
